import { motion, useInView, useMotionValue, useSpring } from "framer-motion";
import { useEffect, useRef, useState } from "react";

export const EASE = [0.16, 1, 0.3, 1];

export function Reveal({ children, delay = 0, y = 28, className = "", once = true }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.15 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function LineMask({ children, delay = 0, className = "", as: Tag = "span" }) {
  return (
    <span className={`line-mask ${className}`}>
      <motion.span
        initial={{ y: "112%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.05, delay, ease: EASE }}
        style={{ display: "block" }}
      >
        <Tag>{children}</Tag>
      </motion.span>
    </span>
  );
}

export function RevealLines({ lines, className = "", lineClassName = "", delay = 0 }) {
  return (
    <span className={className}>
      {lines.map((l, i) => (
        <span key={i} className={`line-mask ${lineClassName}`}>
          <motion.span
            initial={{ y: "112%" }}
            whileInView={{ y: "0%" }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1.0, delay: delay + i * 0.09, ease: EASE }}
            style={{ display: "block" }}
          >
            {l}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export function useCountUp(target, active, duration = 1600) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return val;
}

export function Gauge({ value, label, dark = true, size = 148, sub }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const n = useCountUp(value, inView);
  const stroke = dark ? "#C9A227" : "#0A0A0A";
  const track = dark ? "rgba(255,255,255,0.09)" : "rgba(10,10,10,0.12)";
  const r = (size - 18) / 2;
  const C = 2 * Math.PI * r;
  return (
    <div ref={ref} className="flex flex-col items-center gap-3" data-testid={`gauge-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth="7" />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={stroke}
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={C}
            initial={{ strokeDashoffset: C }}
            animate={inView ? { strokeDashoffset: C * (1 - value / 100) } : {}}
            transition={{ duration: 1.6, ease: EASE }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`font-display font-semibold text-[26px] ${dark ? "text-offwhite" : "text-charcoal"}`} data-testid="gauge-value">
            {n}%
          </span>
        </div>
      </div>
      <p className={`text-[12px] font-semibold text-center leading-tight ${dark ? "text-white/92" : "text-charcoal/70"}`}>{label}</p>
      {sub && <p className="text-[10.5px] font-mono text-sage -mt-2">{sub}</p>}
    </div>
  );
}

export function CountUpNumber({ value, suffix = "", className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const n = useCountUp(value, inView);
  return (
    <span ref={ref} className={className}>
      {n}
      {suffix}
    </span>
  );
}

export function Parallax({ children, amount = 40, className = "" }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 40, damping: 18 });
  const sy = useSpring(y, { stiffness: 40, damping: 18 });
  const ref = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      x.set(((e.clientX - r.left) / r.width - 0.5) * amount);
      y.set(((e.clientY - r.top) / r.height - 0.5) * amount);
    };
    const onLeave = () => {
      x.set(0);
      y.set(0);
    };
    window.addEventListener("mousemove", onMove);
    ref.current?.addEventListener("mouseleave", onLeave);
    return () => window.removeEventListener("mousemove", onMove);
  }, [amount, x, y]);

  return (
    <motion.div ref={ref} style={{ x: sx, y: sy }} className={className}>
      {children}
    </motion.div>
  );
}
